import React, { useState, useContext, useEffect } from "react";
import { AppContext } from "../../context/AppContext";
import "./DatasetAnalytics.scss";

import MetricsCard from "../../components/MetricsCard/MetricsCard";
import Tabs from "../../components/Tabs/Tabs";

import { useNavigate, useLocation } from "react-router-dom";
import HeaderNavigate from "../../components/headerNavigate/HeaderNavigate";
import DatasetTypesDeposited from "../../components/DatasetTypesDeposited/DatasetTypesDeposited";
import Repositories from "../../components/Repositories/Repositories";
import LicenseTypes from "../../components/LicenseTypes/LicenseTypes";
import { useCustomSearchParams } from "../../components/hooks/useCustomSearchParams";
import { SEARCH_PARAMS } from "../../utils/SearchParams";
import { KEY_NAME } from "../../utils/KeyName";
import AxiosService from "../../utils/AxiosService";
import Loader from "../../components/Loader/Loader";
import DataNotFound from "../../components/DataNotFound/DataNotFound";
import SomethingWentWrong from "../../components/SomethingWentWrong/SomethingWentWrong";
import EntityHeader from "../../components/dashboardLayout/EntityHeader/EntityHeader";

const publishers = [
  { label: "All publishers", value: "all" },
  { label: "Elsevier", value: "elsevier" },
  { label: "Springer", value: "springer" },
  { label: "Wiley", value: "wiley" },
];

const TABS_NAME = [
  "Dataset Types Deposited",
  "Repositories",
  "License Types For Submitted Datasets",
];

const DatasetAnalytics = () => {
  const { entityName, metaInfo } = useContext(AppContext);
  const { get, set, setMultiple } = useCustomSearchParams();
  const [shortName, setShortName] = useState(get(SEARCH_PARAMS.SHORT_NAME));
  const [activeTab, setActiveTab] = useState(
    get(SEARCH_PARAMS.TAB) || TABS_NAME[0],
  );
  const navigate = useNavigate();
  const [publisher, setPublisher] = useState("all");
  const [queryData, setQueryData] = useState({
    entityId: get(SEARCH_PARAMS.ENTITY_ID),
  });
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [responseChartData, setResponseChartData] = useState();
  const location = useLocation();

  const funderName = location.state?.funderName || "Funder";

  const axiosService = new AxiosService();

  const renderTabContent = () => {
    switch (activeTab) {
      case TABS_NAME[0]:
        return (
          <DatasetTypesDeposited
            chartData={responseChartData}
            isLoading={isLoading}
          />
        );
      case TABS_NAME[1]:
        return (
          <Repositories chartData={responseChartData} isLoading={isLoading} />
        );
      case TABS_NAME[2]:
        return (
          <LicenseTypes chartData={responseChartData} isLoading={isLoading} />
        );
      default:
        return null;
    }
  };

  useEffect(() => {
    if (activeTab) {
      set(SEARCH_PARAMS.TAB, activeTab);
    }
  }, [activeTab]);

  useEffect(() => {
    if (!entityName) return;

    setIsLoading(true);
    setHasError(false);

    switch (entityName) {
      case KEY_NAME.FUNDER:
        if (activeTab === TABS_NAME[0]) {
          axiosService
            .getFunderDatasetTypeChart(queryData)
            .then((response) => {
              setResponseChartData(response.data);
            })
            .catch((error) => {
              console.error("Error:", error);
              setResponseChartData(null);
              setHasError(true);
            })
            .finally(() => {
              setIsLoading(false);
            });
        } else if (activeTab === TABS_NAME[1]) {
          axiosService
            .getFunderDatasetRepositoriesChart(queryData)
            .then((response) => {
              setResponseChartData(response.data);
            })
            .catch((error) => {
              console.error("Error:", error);
              setResponseChartData(null);
              setHasError(true);
            })
            .finally(() => {
              setIsLoading(false);
            });
        } else if (activeTab === TABS_NAME[2]) {
          axiosService
            .getFunderDatasetLicenseTypesChart(queryData)
            .then((response) => {
              setResponseChartData(response.data);
            })
            .catch((error) => {
              console.error("Error:", error);
              setResponseChartData(null);
              setHasError(true);
            })
            .finally(() => {
              setIsLoading(false);
            });
        }

        break;
      case KEY_NAME.PUBLISHER:
        if (activeTab === TABS_NAME[0]) {
          axiosService
            .getPublisherDatasetTypeChart(queryData)
            .then((response) => {
              setResponseChartData(response.data);
            })
            .catch((error) => {
              console.error("Error:", error);
              setResponseChartData(null);
              setHasError(true);
            })
            .finally(() => {
              setIsLoading(false);
            });
        } else if (activeTab === TABS_NAME[1]) {
          axiosService
            .getPublisherDatasetRepositoriesChart(queryData)
            .then((response) => {
              setResponseChartData(response.data);
            })
            .catch((error) => {
              console.error("Error:", error);
              setResponseChartData(null);
              setHasError(true);
            })
            .finally(() => {
              setIsLoading(false);
            });
        } else if (activeTab === TABS_NAME[2]) {
          axiosService
            .getPublisherDatasetLicenseTypesChart(queryData)
            .then((response) => {
              setResponseChartData(response.data);
            })
            .catch((error) => {
              console.error("Error:", error);
              setResponseChartData(null);
              setHasError(true);
            })
            .finally(() => {
              setIsLoading(false);
            });
        }

        break;
      case KEY_NAME.INSTITUTION:
        if (activeTab === TABS_NAME[0]) {
          axiosService
            .getInstitutionDatasetTypeChart(queryData)
            .then((response) => {
              setResponseChartData(response.data);
            })
            .catch((error) => {
              console.error("Error:", error);
              setResponseChartData(null);
              setHasError(true);
            })
            .finally(() => {
              setIsLoading(false);
            });
        } else if (activeTab === TABS_NAME[1]) {
          axiosService
            .getInstitutionDatasetRepositoriesChart(queryData)
            .then((response) => {
              setResponseChartData(response.data);
            })
            .catch((error) => {
              console.error("Error:", error);
              setResponseChartData(null);
              setHasError(true);
            })
            .finally(() => {
              setIsLoading(false);
            });
        } else if (activeTab === TABS_NAME[2]) {
          axiosService
            .getInstitutionDatasetLicenseTypesChart(queryData)
            .then((response) => {
              setResponseChartData(response.data);
            })
            .catch((error) => {
              console.error("Error:", error);
              setResponseChartData(null);
              setHasError(true);
            })
            .finally(() => {
              setIsLoading(false);
            });
        }
        break;

      default:
        break;
    }
  }, [entityName, activeTab]);

  return (
    <div className="dataset-analytics">
      <HeaderNavigate
        label={`Funder Dashboard / ${
          get(SEARCH_PARAMS.ENTITY_NAME) || funderName
        }`}
      />

      <div className="bg-light text-start py-4 px-3 mb-4">
        <EntityHeader
          metaInfo={metaInfo}
          dashboardType={entityName}
          title={get(SEARCH_PARAMS.ENTITY_NAME)}
        />
      </div>

      <div className="dataset-analytics-container text-start me-5">
        <h4 className="text-muted mb-4">Dataset Analytics</h4>
        {metaInfo && (
          <div className="container px-0">
            <div className="row">
              <div className="col-12 col-sm-10">
                {responseChartData && (
                  <MetricsCard
                    title="Total Datasets to Date"
                    value={
                      responseChartData?.total?.toLocaleString("en-US") || ""
                    }
                    change={`${responseChartData?.percentIncrease > 0 ? "+" + responseChartData?.percentIncrease : 0}%`}
                    changeLabel="since last month"
                  />
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="tab-container">
        <Tabs tabs={TABS_NAME} active={activeTab} onChange={setActiveTab} />
      </div>
      {isLoading ? (
        <Loader />
      ) : hasError ? (
        <SomethingWentWrong />
      ) : responseChartData ? (
        <div className="tab-content">{renderTabContent()}</div>
      ) : (
        <DataNotFound />
      )}
    </div>
  );
};

export default DatasetAnalytics;
